// Upload-time on-device scanning: turns a File into face/person detections and
// uploads the resulting crops + sidecar to the presigned prescan targets. Wraps
// the ported scan pipeline (scanController + sidecar) for the upload manager.
//
// Everything here is best-effort and fail-safe (mirrors photohawk-admin's
// uploadManager): any failure means the photo is uploaded plain and the server
// scans it as before. On-device scanning can never block or fail an upload.

import { probe, scanFile, shutdownScanPool } from "./scanController";
import { buildSidecar } from "./sidecar";
import { putArtifact, type PrescanTargets } from "@/lib/api/content";

// Re-exported so the upload manager can free the scan workers' memory (ONNX heaps
// + in-flight bitmaps) when the queue goes idle or is cancelled.
export { shutdownScanPool };

interface TextDetection {
  text: string;
  confidence: number;
  box: { x: number; y: number; w: number; h: number };
  width: number;
  height: number;
}

interface Detection {
  confidence: number;
  box: number[];
  cropBlob?: Blob;
  /** Actual (capped) crop output pixels — recorded in the sidecar for the montage layout. */
  cropWidth?: number;
  cropHeight?: number;
  /** Relative sharpness score the client used to keep this detection (999 if the sole detection). */
  sharpness?: number;
  /** On-device OCR text detections on this person's crop (Phase 1); undefined for faces. */
  text?: TextDetection[];
}

interface SidecarCrop {
  key: string;
  box: number[];
  confidence: number;
  width?: number;
  height?: number;
  sharpnessLevel?: number;
  text?: TextDetection[];
}

export interface ScanResult {
  faces: Detection[];
  people: Detection[];
}

let probePromise: Promise<boolean> | null = null;

/** Whether on-device scanning is viable on this device. Memoised for the session. */
export const probeScan = (): Promise<boolean> => {
  if (!probePromise) {
    probePromise = probe()
      .then((p: { ok?: boolean }) => !!p?.ok)
      .catch(() => false);
  }
  return probePromise;
};

/**
 * Detect faces + people on-device for one file. Returns `null` on any failure
 * (the caller then uploads the file plain, and the server scans it).
 */
export const scanFileForUpload = async (
  file: File,
  wantPeople = true,
  wantFaces = true,
  // Text OCR is wanted exactly when we detect people (both are driven by the gallery's
  // needsPeople / textSearch policy), so it defaults to wantPeople. The controller's flag
  // (textScanEnabled) still governs whether it actually runs — Phase 1, OFF by default.
  wantText = wantPeople,
): Promise<ScanResult | null> => {
  try {
    const res = (await scanFile(file, undefined, { wantPeople, wantFaces, wantText })) as ScanResult;
    if (res && Array.isArray(res.faces)) {
      // Phase 1 is emit-only (the server still OCRs and does NOT yet trust these reads),
      // so surface any on-device bib/text detections in devtools so testers can eyeball
      // parity. Only logs when there's text — no noise on ordinary galleries.
      const texts = (res.people || []).flatMap((p) => (p.text || []).map((t) => t.text));
      if (texts.length) {
        // eslint-disable-next-line no-console
        console.log(`[photohawk scan] on-device bib text for ${file.name}:`, texts);
      }
      return res;
    }
    return null;
  } catch {
    return null;
  }
};

// PUT each detection's crop to its presigned target, returning sidecar entries
// { key, box, confidence }. Aborts (→ null) if any crop fails to land — a missing
// crop would break the server's batch face indexing.
const putCrops = async (
  dets: Detection[],
  targets: { key: string; url: string }[],
  signal?: AbortSignal,
): Promise<SidecarCrop[] | null> => {
  const out: SidecarCrop[] = [];
  for (let i = 0; i < dets.length; i += 1) {
    const target = targets[i];
    const blob = dets[i]?.cropBlob;
    if (!target || !(blob instanceof Blob)) return null;
    const ok = await putArtifact(target.url, blob, "image/jpeg", signal);
    if (!ok) return null;
    // width/height are the crop's actual (capped) output pixels — scanFacesBatch
    // lays each crop out at these dims when montaging for Rekognition.
    out.push({
      key: target.key,
      box: dets[i].box,
      confidence: dets[i].confidence,
      width: dets[i].cropWidth,
      height: dets[i].cropHeight,
      sharpnessLevel: dets[i].sharpness,
      // On-device OCR text (Phase 1) — carried onto the person's sidecar entry so the
      // server can compare it against its own PaddleOCR (it does NOT yet trust it).
      ...(dets[i].text ? { text: dets[i].text } : {}),
    });
  }
  return out;
};

/**
 * Upload an entry's crops + sidecar to the presigned prescan targets. Returns
 * TRUE only if every crop AND the sidecar landed — the caller then stores the
 * photo via the prescanned content POST. Any failure → false → plain upload.
 */
export const uploadPrescanArtifacts = async (
  scan: ScanResult,
  prescan: PrescanTargets,
  signal?: AbortSignal,
): Promise<boolean> => {
  try {
    if (!prescan?.sidecar?.url) return false;
    const faceOut = await putCrops(scan.faces || [], prescan.faceCrops || [], signal);
    if (faceOut === null) return false;
    const peopleOut = await putCrops(scan.people || [], prescan.personCrops || [], signal);
    if (peopleOut === null) return false;

    const sidecar = buildSidecar({ faces: faceOut, people: peopleOut }) as {
      contentGuid?: string;
    };
    if (prescan.contentGuid) sidecar.contentGuid = prescan.contentGuid;
    return putArtifact(prescan.sidecar.url, JSON.stringify(sidecar), "application/json", signal);
  } catch {
    return false;
  }
};
